<template>
  <div class="kb-page admin-dashboard-modern">
    <!-- Page Header -->
    <div class="page-header">
      <div class="page-header-content">
        <div class="page-icon">
          <i class="fa fa-book"></i>
        </div>
        <div class="page-header-text">
          <h2 class="page-title">Knowledge</h2>
          <p class="page-subtitle">Review and approve what your AI receptionist is allowed to say</p>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoadingKb && (!kbItems || kbItems.length === 0)" class="panel-card">
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading your knowledge base...</div>
      </div>
    </div>

    <!-- Not enabled (403) -->
    <div v-else-if="kbAccessState === 'not-enabled'" class="panel-card">
      <div class="empty-state">
        <div class="empty-icon">🔒</div>
        <div class="empty-text">AI Knowledge — Coming soon</div>
        <div class="empty-subtext">
          With AI Knowledge, you'll be able to review and approve everything
          your AI receptionist can say.
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="kbError" class="panel-card">
      <div class="error-state">
        <div class="error-icon">⚠️</div>
        <div class="error-text">Couldn't load the knowledge base</div>
        <div class="error-message">{{ kbError }}</div>
        <button class="btn retry-btn" @click="loadKb">Try Again</button>
      </div>
    </div>

    <!-- Main -->
    <template v-else>
      <!-- "Nothing is live until you approve it" banner -->
      <div class="approve-banner">
        <i class="fa fa-shield-alt banner-icon"></i>
        <div class="banner-text">
          <strong>Nothing is live to your AI receptionist until you approve it.</strong>
          Review each item below, edit anything that's off, then approve it.
        </div>
      </div>

      <!-- Unknown-fallback selector -->
      <div class="panel-card">
        <div class="panel-header">
          <h3 class="panel-title">
            <i class="fa fa-question-circle panel-icon"></i>
            When the receptionist doesn't know the answer
          </h3>
        </div>
        <div class="panel-body">
          <div class="fallback-row">
            <label class="filter-label" for="kb-unknown-default">It should</label>
            <select
              id="kb-unknown-default"
              class="form-select fallback-select"
              :value="kbUnknownDefault"
              :disabled="isSavingFallback"
              @change="onUnknownDefaultChange($event.target.value)"
            >
              <option v-for="opt in unknownDefaultOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
            <span v-if="isSavingFallback" class="saving-hint">Saving…</span>
          </div>
        </div>
      </div>

      <!-- Toolbar: counts + bulk actions + add -->
      <div class="panel-card">
        <div class="kb-toolbar">
          <div class="toolbar-left">
            <span class="kb-count">{{ kbItems.length }} item{{ kbItems.length === 1 ? '' : 's' }}</span>
            <span v-if="needsReviewTotal > 0" class="kb-count needs-review-count">
              {{ needsReviewTotal }} need{{ needsReviewTotal === 1 ? 's' : '' }} your review
            </span>
            <span v-if="selectedIds.length > 0" class="kb-count selected-count">
              {{ selectedIds.length }} selected
            </span>
          </div>
          <div class="toolbar-right">
            <button
              v-if="selectedIds.length > 0"
              class="btn apply-btn"
              :disabled="isBusy"
              @click="approveSelected"
            >
              <i class="fa fa-check"></i>
              Approve selected
            </button>
            <button
              v-if="highConfidenceIds.length > 0"
              class="btn ghost-btn"
              :disabled="isBusy"
              @click="approveHighConfidence"
            >
              <i class="fa fa-bolt"></i>
              Approve all high-confidence ({{ highConfidenceIds.length }})
            </button>
            <button class="btn apply-btn" :disabled="isBusy" @click="openAdd">
              <i class="fa fa-plus"></i>
              Add manually
            </button>
          </div>
        </div>
      </div>

      <!-- Seed panels: upload a document / import the website -->
      <div class="panel-card">
        <div class="panel-header">
          <h3 class="panel-title">
            <i class="fa fa-magic panel-icon"></i>
            Add answers from your documents or website
          </h3>
        </div>
        <div class="panel-body">
          <p class="seed-intro">
            We'll pull likely questions and answers and add them below for your review.
            Nothing goes live until you approve it.
          </p>

          <div class="seed-grid">
            <!-- Document upload -->
            <div class="seed-panel">
              <div class="seed-panel-head">
                <i class="fa fa-file-alt seed-panel-icon"></i>
                <div class="seed-panel-title">Upload a document</div>
              </div>
              <div class="seed-panel-desc">A PDF or Word file — like a new-patient packet or office FAQ.</div>

              <input
                ref="kbDocInput"
                type="file"
                class="seed-file-hidden"
                :accept="docAcceptAttr"
                :disabled="isSeedBusy"
                @change="onDocPicked"
              />
              <button
                class="btn apply-btn seed-action"
                :disabled="isSeedBusy"
                @click="triggerDocPicker"
              >
                <span v-if="docBusy" class="btn-spinner"></span>
                <i v-else class="fa fa-upload"></i>
                {{ docBusy ? docPhaseLabel : 'Choose a file (PDF or Word)' }}
              </button>

              <div v-if="docError" class="seed-inline-error">
                <i class="fa fa-exclamation-circle"></i> {{ docError }}
              </div>
              <div v-if="docFileName && !docError" class="seed-file-name">{{ docFileName }}</div>
            </div>

            <!-- Website import -->
            <div class="seed-panel">
              <div class="seed-panel-head">
                <i class="fa fa-globe seed-panel-icon"></i>
                <div class="seed-panel-title">Import from your website</div>
              </div>
              <div class="seed-panel-desc">We'll read a few key pages from your practice site.</div>

              <div class="seed-url-row">
                <input
                  v-model="scrapeUrl"
                  type="url"
                  class="form-control seed-url-input"
                  placeholder="https://yourpractice.com"
                  :disabled="isSeedBusy"
                  @keyup.enter="importWebsite"
                  @input="scrapeError = ''"
                />
                <button
                  class="btn apply-btn seed-action seed-url-btn"
                  :disabled="isSeedBusy"
                  @click="importWebsite"
                >
                  <span v-if="scrapeBusy" class="btn-spinner"></span>
                  <i v-else class="fa fa-download"></i>
                  {{ scrapeBusy ? 'Importing…' : 'Import' }}
                </button>
              </div>

              <div v-if="scrapeError" class="seed-inline-error">
                <i class="fa fa-exclamation-circle"></i> {{ scrapeError }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty (enabled but no rows) -->
      <div v-if="kbItems.length === 0" class="panel-card">
        <div class="empty-state">
          <div class="empty-icon">📚</div>
          <div class="empty-text">Your knowledge base is empty</div>
          <div class="empty-subtext">
            Add an answer manually above, or upload a document / import your website to get started.
          </div>
        </div>
      </div>

      <!-- Grouped rows -->
      <div
        v-for="group in groupedRows"
        :key="group.key"
        class="panel-card kb-group"
      >
        <div class="panel-header">
          <h3 class="panel-title">
            <i class="fa fa-folder-open panel-icon"></i>
            {{ group.label }}
          </h3>
          <span v-if="group.needsReviewCount > 0" class="result-count needs-review-pill">
            {{ group.needsReviewCount }} to review
          </span>
        </div>
        <div class="panel-body kb-group-body">
          <div
            v-for="row in group.rows"
            :key="row.id"
            class="kb-row"
            :class="{ 'kb-row-needs-review': row.status === 'needs_review', 'kb-row-disabled': !row.enabled }"
          >
            <div class="kb-row-select">
              <input
                type="checkbox"
                :checked="selectedSet.has(row.id)"
                :aria-label="'Select item ' + row.id"
                @change="toggleSelect(row.id)"
              />
            </div>

            <div class="kb-row-main">
              <div class="kb-row-meta">
                <span class="status-badge" :class="'badge-' + statusBadge(row.status).variant">
                  {{ statusBadge(row.status).label }}
                </span>
                <span class="source-chip">{{ sourceChip(row.source) }}</span>
                <span v-if="row.sensitive" class="sensitive-chip" title="Contains potentially sensitive info">
                  <i class="fa fa-lock"></i> sensitive
                </span>
                <span v-if="!row.enabled" class="disabled-chip">disabled</span>
                <span v-if="row.updated_at" class="updated-at">Updated {{ formatUpdatedAt(row.updated_at) }}</span>
              </div>

              <div v-if="row.question" class="kb-question">{{ row.question }}</div>
              <div class="kb-answer">{{ row.answer }}</div>
            </div>

            <div class="kb-row-actions">
              <button
                v-if="row.status !== 'approved'"
                class="action-btn approve-btn"
                :disabled="isBusy"
                title="Approve — makes this live to the receptionist"
                @click="approveOne(row)"
              >
                <i class="fa fa-check"></i> Approve
              </button>
              <button class="action-btn edit-btn" :disabled="isBusy" title="Edit" @click="openEdit(row)">
                <i class="fa fa-pencil-alt"></i>
              </button>
              <button
                class="action-btn toggle-btn"
                :disabled="isBusy"
                :title="row.enabled ? 'Disable' : 'Enable'"
                @click="toggleEnabled(row)"
              >
                <i :class="row.enabled ? 'fa fa-toggle-on' : 'fa fa-toggle-off'"></i>
              </button>
              <button class="action-btn delete-btn" :disabled="isBusy" title="Delete" @click="confirmDelete(row)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Add / Edit modal -->
    <b-modal
      id="kb-edit-modal"
      v-model="showEditModal"
      :title="editForm.id ? 'Edit knowledge item' : 'Add knowledge item'"
      centered
      no-close-on-backdrop
      @ok.prevent="saveEdit"
    >
      <div class="kb-form">
        <div class="form-field">
          <label class="filter-label">Type</label>
          <select v-model="editForm.type" class="form-select form-control">
            <option value="fact">Fact</option>
            <option value="faq">Question &amp; answer</option>
          </select>
        </div>

        <div class="form-field">
          <label class="filter-label">Category</label>
          <select v-model="editForm.category" class="form-select form-control">
            <option v-for="cat in categoryOptions" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
          </select>
        </div>

        <div v-if="editForm.type === 'faq'" class="form-field">
          <label class="filter-label">Question</label>
          <input v-model="editForm.question" type="text" class="form-control" placeholder="e.g. Do you take walk-ins?" />
        </div>

        <div class="form-field">
          <label class="filter-label">{{ editForm.type === 'faq' ? 'Answer' : 'Fact' }}</label>
          <textarea
            v-model="editForm.answer"
            rows="4"
            class="form-control"
            :placeholder="editForm.type === 'faq' ? 'What should the receptionist say?' : 'e.g. We are open Mon–Thu 8am–5pm.'"
          ></textarea>
        </div>

        <div class="form-field form-field-inline">
          <input id="kb-sensitive" v-model="editForm.sensitive" type="checkbox" />
          <label for="kb-sensitive" class="inline-label">Mark as sensitive (handle with extra care)</label>
        </div>

        <p class="form-hint">
          New items are saved as a draft. They go live only after you approve them.
        </p>
      </div>

      <template #modal-footer="{ cancel, ok }">
        <button class="btn btn-secondary" @click="cancel()">Cancel</button>
        <button class="btn apply-btn" :disabled="!canSaveEdit || isBusy" @click="ok()">
          {{ editForm.id ? 'Save changes' : 'Add as draft' }}
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import mixin from '../../../../mixin.js';
import {
  groupKbRows,
  statusBadgeFor,
  sourceChipFor,
  collectHighConfidenceApprovalIds,
  collectBulkApprovalIds,
  KB_CATEGORY_ORDER,
  KB_CATEGORY_LABELS,
  KB_UNKNOWN_DEFAULT_OPTIONS,
  KB_DOC_ACCEPT_ATTR,
  validateKbDocFile,
  validateKbScrapeUrl,
  ingestDocSummaryMessage,
  ingestScrapeSummaryMessage,
} from '@/utils/receptionistKb';
import { uploadToS3 } from '@/utils/fileUpload';

export default {
  name: 'KnowledgeBase',
  mixins: [mixin],
  data() {
    return {
      selectedIds: [],
      isBusy: false,
      isSavingFallback: false,
      showEditModal: false,
      editForm: this.blankForm(),
      unknownDefaultOptions: KB_UNKNOWN_DEFAULT_OPTIONS,
      categoryOptions: KB_CATEGORY_ORDER.map(value => ({ value, label: KB_CATEGORY_LABELS[value] || value })),
      docAcceptAttr: KB_DOC_ACCEPT_ATTR,
      // Document-upload panel state.
      docBusy: false,
      docPhase: '', // 'uploading' | 'extracting'
      docError: '',
      docFileName: '',
      // Website-import panel state.
      scrapeUrl: '',
      scrapeBusy: false,
      scrapeError: '',
    };
  },
  computed: {
    ...mapState('botAdmin', {
      kbItems: state => state.kbItems,
      isLoadingKb: state => state.isLoadingKb,
      kbError: state => state.kbError,
      kbAccessState: state => state.kbAccessState,
      kbUnknownDefault: state => state.kbUnknownDefault,
    }),
    groupedRows() {
      return groupKbRows(this.kbItems);
    },
    needsReviewTotal() {
      return (this.kbItems || []).filter(r => r.status === 'needs_review').length;
    },
    highConfidenceIds() {
      return collectHighConfidenceApprovalIds(this.kbItems);
    },
    selectedSet() {
      return new Set(this.selectedIds);
    },
    canSaveEdit() {
      return !!(this.editForm.answer && this.editForm.answer.trim());
    },
    siteTimezone() {
      const tz = this.allSettings && this.allSettings.GENERAL_TIMEZONE && this.allSettings.GENERAL_TIMEZONE.PropertyValue;
      return tz ? tz.toUpperCase() : moment.tz.guess();
    },
    // True while EITHER seed panel is running — disables both panels (and the
    // general curate `isBusy` is also raised so the list actions disable too).
    isSeedBusy() {
      return this.docBusy || this.scrapeBusy;
    },
    docPhaseLabel() {
      return this.docPhase === 'extracting' ? 'Reading your document…' : 'Uploading…';
    },
  },
  methods: {
    ...mapActions('botAdmin', [
      'fetchKb',
      'upsertKb',
      'approveKb',
      'setKbEnabled',
      'deleteKb',
      'fetchKbSettings',
      'setKbUnknownDefault',
      'ingestKbDoc',
      'ingestKbScrape',
    ]),
    blankForm() {
      return { id: null, type: 'fact', category: 'other', question: '', answer: '', sensitive: false };
    },
    statusBadge(status) {
      return statusBadgeFor(status);
    },
    sourceChip(source) {
      return sourceChipFor(source);
    },
    formatUpdatedAt(value) {
      if (!value) return '';
      const m = moment.utc(value);
      if (!m.isValid()) return '';
      return m.tz(this.siteTimezone).format('MMM D, YYYY');
    },
    async loadKb() {
      if (!this.siteID) return;
      await this.fetchKb({ siteId: this.siteID });
      // Settings only matter when the KB is actually enabled (avoid a 2nd 403 spam).
      if (this.kbAccessState === 'ok') {
        await this.fetchKbSettings({ siteId: this.siteID });
      }
      // Drop any selections that no longer exist after a refresh.
      const live = new Set((this.kbItems || []).map(r => r.id));
      this.selectedIds = this.selectedIds.filter(id => live.has(id));
    },
    toggleSelect(id) {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(idx, 1);
    },
    async runAndRefresh(fn, { success } = {}) {
      this.isBusy = true;
      try {
        const result = await fn();
        if (result && result.success === false) {
          this.$toast && this.$toast.error(result.error || 'Something went wrong.');
          return false;
        }
        if (success) this.$toast && this.$toast.success(success);
        await this.loadKb();
        return true;
      } finally {
        this.isBusy = false;
      }
    },
    async approveOne(row) {
      await this.runAndRefresh(
        () => this.approveKb({ siteId: this.siteID, ids: [row.id] }),
        { success: 'Approved — it\'s now live to your receptionist.' }
      );
    },
    async approveSelected() {
      const ids = collectBulkApprovalIds(this.selectedIds, this.kbItems);
      if (ids.length === 0) {
        this.$toast && this.$toast.info('Nothing to approve in your selection.');
        return;
      }
      const ok = await this.runAndRefresh(
        () => this.approveKb({ siteId: this.siteID, ids }),
        { success: `Approved ${ids.length} item${ids.length === 1 ? '' : 's'}.` }
      );
      if (ok) this.selectedIds = [];
    },
    async approveHighConfidence() {
      const ids = this.highConfidenceIds;
      if (ids.length === 0) return;
      await this.runAndRefresh(
        () => this.approveKb({ siteId: this.siteID, ids }),
        { success: `Approved ${ids.length} high-confidence item${ids.length === 1 ? '' : 's'}.` }
      );
    },
    async toggleEnabled(row) {
      await this.runAndRefresh(
        () => this.setKbEnabled({ siteId: this.siteID, id: row.id, enabled: !row.enabled }),
        { success: row.enabled ? 'Item disabled.' : 'Item enabled.' }
      );
    },
    async confirmDelete(row) {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Delete this item? Your AI receptionist will no longer be able to use it. This cannot be undone.',
        {
          title: 'Delete knowledge item',
          okTitle: 'Delete',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        }
      );
      if (!ok) return;
      this.runAndRefresh(
        () => this.deleteKb({ siteId: this.siteID, id: row.id }),
        { success: 'Item deleted.' }
      );
    },
    openAdd() {
      this.editForm = this.blankForm();
      this.showEditModal = true;
    },
    openEdit(row) {
      this.editForm = {
        id: row.id,
        type: row.type || 'fact',
        category: row.category || 'other',
        question: row.question || '',
        answer: row.answer || '',
        sensitive: !!row.sensitive,
      };
      this.showEditModal = true;
    },
    async saveEdit() {
      if (!this.canSaveEdit) return;
      // source:'manual' for a brand-new row; on edit, the backend keeps the row's
      // existing source/status — buildUpsertRow (store) never sends status/enabled.
      const row = {
        id: this.editForm.id || undefined,
        type: this.editForm.type,
        category: this.editForm.category,
        question: this.editForm.type === 'faq' ? this.editForm.question : '',
        answer: this.editForm.answer,
        source: 'manual',
        sensitive: this.editForm.sensitive,
      };
      const ok = await this.runAndRefresh(
        () => this.upsertKb({ siteId: this.siteID, row }),
        { success: this.editForm.id ? 'Changes saved.' : 'Added as a draft — approve it to go live.' }
      );
      if (ok) this.showEditModal = false;
    },
    async onUnknownDefaultChange(value) {
      if (!value || value === this.kbUnknownDefault) return;
      this.isSavingFallback = true;
      try {
        const result = await this.setKbUnknownDefault({ siteId: this.siteID, value });
        if (result && result.success === false) {
          this.$toast && this.$toast.error(result.error || 'Could not save that setting.');
        } else {
          this.$toast && this.$toast.success('Saved.');
        }
      } finally {
        this.isSavingFallback = false;
      }
    },

    // ---- E3: document upload ------------------------------------------------
    triggerDocPicker() {
      if (this.isSeedBusy) return;
      this.docError = '';
      if (this.$refs.kbDocInput) this.$refs.kbDocInput.click();
    },
    async onDocPicked(event) {
      const input = event && event.target;
      const file = input && input.files && input.files[0];
      // Always clear the input value so re-picking the SAME file fires `change`
      // again (browsers suppress an identical re-selection otherwise).
      if (input) input.value = '';
      if (!file) return;

      // Client-side validation (extension): reject non-PDF/Word BEFORE any upload.
      const check = validateKbDocFile(file);
      if (!check.valid) {
        this.docError = check.error;
        this.docFileName = '';
        return;
      }

      this.docError = '';
      this.docFileName = file.name;
      this.docBusy = true;
      this.isBusy = true; // also disable the list actions while seeding.
      try {
        // Phase 1 — upload to S3 (RECEPTIONIST_KB -> the bucket the bot reads).
        this.docPhase = 'uploading';
        let uploaded;
        try {
          uploaded = await uploadToS3({
            file,
            siteID: this.siteID,
            teamioType: 'RECEPTIONIST_KB',
          });
        } catch (err) {
          this.docError = 'We couldn\'t upload that file. Please try again.';
          return;
        }

        // Phase 2 — ask the bot to extract candidates from the uploaded doc.
        this.docPhase = 'extracting';
        const result = await this.ingestKbDoc({
          siteId: this.siteID,
          s3Key: uploaded.key,
          filename: uploaded.name,
        });

        if (!result || result.success === false) {
          // 403 -> re-list so the friendly not-enabled state takes over the whole
          // tab; otherwise show the bot's friendly inline message (unsupported /
          // unreadable doc).
          if (result && result.notEnabled) {
            await this.loadKb();
            return;
          }
          this.docError = (result && result.error) || 'We couldn\'t read that document.';
          return;
        }

        // Refresh so the new needs_review candidates appear, then toast the count.
        await this.loadKb();
        this.docFileName = '';
        this.$toast && this.$toast.success(ingestDocSummaryMessage(result));
      } finally {
        this.docBusy = false;
        this.docPhase = '';
        this.isBusy = false;
      }
    },

    // ---- E3: website import -------------------------------------------------
    async importWebsite() {
      if (this.isSeedBusy) return;
      const check = validateKbScrapeUrl(this.scrapeUrl);
      if (!check.valid) {
        this.scrapeError = check.error;
        return;
      }

      this.scrapeError = '';
      this.scrapeBusy = true;
      this.isBusy = true;
      try {
        const result = await this.ingestKbScrape({ siteId: this.siteID, url: check.url });
        if (!result || result.success === false) {
          // 403 -> re-list into the friendly not-enabled state; otherwise inline error.
          if (result && result.notEnabled) {
            await this.loadKb();
            return;
          }
          this.scrapeError = (result && result.error) || 'We couldn\'t import that website.';
          return;
        }
        await this.loadKb();
        this.$toast && this.$toast.success(ingestScrapeSummaryMessage(result));
      } finally {
        this.scrapeBusy = false;
        this.isBusy = false;
      }
    },
  },
  mounted() {
    if (this.siteID) this.loadKb();
  },
  watch: {
    siteID(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.selectedIds = [];
        this.loadKb();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../AppointmentCalendarModern.scss';

.kb-page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1rem;
  font-family: var(--aid-font);
}

/* Header (mirrors ConversationList) */
.page-header { margin-bottom: 0; }
.page-header-content { display: flex; align-items: center; gap: 1rem; }
.page-icon {
  width: 44px; height: 44px;
  background: var(--aid-accent-soft);
  border-radius: 10px; display: flex; align-items: center; justify-content: center;
  box-shadow: none;
  i { color: var(--aid-accent); font-size: 1.15rem; }
}
.page-header-text { display: flex; flex-direction: column; gap: 0.25rem; }
.page-title { font-size: 18px; margin: 0; font-weight: 600; color: var(--aid-text); letter-spacing: -0.1px; }
.page-subtitle { margin: 0; font-size: 13px; color: var(--aid-text-mute); }

/* Approve banner — tasteful teal, in the restrained palette (no bright gradient). */
.approve-banner {
  display: flex; align-items: flex-start; gap: 0.75rem;
  background: var(--aid-accent-soft);
  border: 1px solid #cfe6e2;
  border-left: 3px solid var(--aid-accent);
  border-radius: 10px; padding: 0.875rem 1.1rem; color: var(--aid-accent-ink);
}
.banner-icon { font-size: 1.05rem; margin-top: 0.1rem; color: var(--aid-accent); }
.banner-text { font-size: 13px; line-height: 1.45; }
.banner-text strong { display: block; color: var(--aid-accent-ink); margin-bottom: 0.15rem; }

/* Panel card */
.panel-card { background: var(--aid-surface); border-radius: 10px; border: 1px solid var(--aid-border); box-shadow: var(--aid-shadow-card); overflow: hidden; }
.panel-header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid var(--aid-border); background: var(--aid-surface2); }
.panel-title { display: flex; align-items: center; gap: 0.5rem; font-size: 14px; font-weight: 600; color: var(--aid-text); margin: 0; }
.panel-icon { color: var(--aid-accent); font-size: 0.875rem; }
.panel-body { padding: 1.25rem; }
.result-count { font-size: 12px; font-weight: 500; color: var(--aid-text-mute); background: var(--aid-surface2); padding: 0.2rem 0.7rem; border-radius: 999px; }
.needs-review-pill { background: var(--aid-warn-bg); color: var(--aid-warn-text); }

.filter-label { font-size: 11px; font-weight: 600; color: var(--aid-text-mute); text-transform: uppercase; letter-spacing: 0.5px; }

/* Fallback selector */
.fallback-row { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.fallback-select { max-width: 360px; }
.saving-hint { font-size: 12px; color: var(--aid-text-dim); }

/* Select — TxPresenter look: 6px radius, soft border, our chevron, teal focus ring. */
.form-select {
  padding: 7px 30px 7px 10px; font-size: 13px; line-height: 1.3;
  border: 1px solid var(--aid-border); border-radius: 6px; background-color: var(--aid-surface); appearance: none; -webkit-appearance: none; -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2010%206%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M0%200l5%206%205-6z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat; background-position: right 10px center; background-size: 10px 6px; color: var(--aid-text);
  &:focus { outline: none; border-color: var(--aid-accent); box-shadow: 0 0 0 2px rgba(15,110,99,0.12); }
}

/* Toolbar */
.kb-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 0.875rem 1.25rem; flex-wrap: wrap; }
.toolbar-left { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.toolbar-right { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.kb-count { font-size: 12px; font-weight: 600; color: var(--aid-text-mute); background: var(--aid-surface2); padding: 0.2rem 0.6rem; border-radius: 999px; }
.kb-count.needs-review-count { background: var(--aid-warn-bg); color: var(--aid-warn-text); }
.kb-count.selected-count { background: var(--aid-accent-soft); color: var(--aid-accent-ink); }

/* Buttons — token system: secondary is the default (.btn), primary is the teal
   .apply-btn. 8px radius, 13px/500, flat teal (no bright gradient/shadow). */
.btn { padding: 6px 12px; border-radius: 8px; font-size: 13px; cursor: pointer; font-weight: 500; display: inline-flex; align-items: center; gap: 0.4rem; border: 1px solid var(--aid-border); background: var(--aid-surface); color: var(--aid-text); transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease; }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.apply-btn { background: var(--aid-accent); color: #fff; border-color: var(--aid-accent); box-shadow: none; }
.apply-btn:hover:not(:disabled) { background: var(--aid-accent-ink); border-color: var(--aid-accent-ink); }
.ghost-btn { background: var(--aid-surface); border: 1px solid var(--aid-border); color: var(--aid-text); }
.ghost-btn:hover:not(:disabled) { background: var(--aid-surface2); border-color: var(--aid-border-strong); }
.btn-secondary { background: var(--aid-surface); border: 1px solid var(--aid-border); color: var(--aid-text); }
.btn-secondary:hover { background: var(--aid-surface2); }
.retry-btn { background: var(--aid-accent); color: #fff; border-color: var(--aid-accent); }
.retry-btn:hover:not(:disabled) { background: var(--aid-accent-ink); border-color: var(--aid-accent-ink); }

/* Groups + rows */
.kb-group-body { padding: 0; }
.kb-row { display: flex; align-items: flex-start; gap: 0.75rem; padding: 1rem 1.25rem; border-bottom: 1px solid var(--aid-border); }
.kb-row:last-child { border-bottom: none; }
.kb-row-needs-review { background: rgba(180, 83, 9, 0.05); }
.kb-row-disabled { opacity: 0.62; }
.kb-row-select { padding-top: 0.2rem; }
.kb-row-select input { width: 16px; height: 16px; cursor: pointer; accent-color: var(--aid-accent); }
.kb-row-main { flex: 1; min-width: 0; }
.kb-row-meta { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; margin-bottom: 0.4rem; }

/* Status badges — palette mapping per spec:
   draft → muted gray, needs-review → amber, approved → teal/success. */
.status-badge { display: inline-block; padding: 0.2rem 0.6rem; border-radius: 999px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
.badge-draft { background: var(--aid-surface2); color: var(--aid-text-dim); }
.badge-needs-review { background: var(--aid-warn-bg); color: var(--aid-warn-text); }
.badge-approved { background: var(--aid-accent-soft); color: var(--aid-accent-ink); }

/* Source + meta chips as pills, in the restrained palette. */
.source-chip { display: inline-block; padding: 0.15rem 0.55rem; border-radius: 999px; font-size: 11px; font-weight: 600; background: var(--aid-surface2); color: var(--aid-text-mute); }
.sensitive-chip { display: inline-flex; align-items: center; gap: 0.2rem; padding: 0.15rem 0.5rem; border-radius: 999px; font-size: 11px; font-weight: 600; background: var(--aid-danger-bg); color: var(--aid-danger-text); }
.disabled-chip { padding: 0.15rem 0.5rem; border-radius: 999px; font-size: 11px; font-weight: 600; background: var(--aid-surface2); color: var(--aid-text-mute); }
.updated-at { font-size: 11px; color: var(--aid-text-dim); }

.kb-question { font-weight: 600; color: var(--aid-text); font-size: 13px; margin-bottom: 0.2rem; word-break: break-word; }
.kb-answer { color: var(--aid-text-mute); font-size: 13px; line-height: 1.5; word-break: break-word; }

.kb-row-actions { display: flex; align-items: center; gap: 0.35rem; flex-shrink: 0; }
.action-btn { background: var(--aid-surface); border: 1px solid var(--aid-border); padding: 0.35rem 0.5rem; cursor: pointer; font-size: 12px; border-radius: 6px; color: var(--aid-text-mute); display: inline-flex; align-items: center; gap: 0.3rem; transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease; }
.action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.approve-btn { background: var(--aid-accent-soft); color: var(--aid-accent-ink); border-color: transparent; font-weight: 600; }
.approve-btn:hover:not(:disabled) { background: #d9eae7; }
.edit-btn:hover:not(:disabled) { background: var(--aid-surface2); border-color: var(--aid-border-strong); color: var(--aid-text); }
.toggle-btn:hover:not(:disabled) { background: var(--aid-surface2); border-color: var(--aid-border-strong); }
.delete-btn { color: var(--aid-danger-text); }
.delete-btn:hover:not(:disabled) { background: var(--aid-danger-bg); border-color: #fecaca; }

/* Form */
.kb-form { display: flex; flex-direction: column; gap: 0.9rem; }
.form-field { display: flex; flex-direction: column; gap: 0.35rem; }
.form-field-inline { flex-direction: row; align-items: center; gap: 0.5rem; }
.form-field-inline input[type="checkbox"] { accent-color: var(--aid-accent); }
.inline-label { margin: 0; font-size: 13px; color: var(--aid-text-mute); }
.form-control { padding: 7px 10px; font-size: 13px; border: 1px solid var(--aid-border); border-radius: 6px; color: var(--aid-text); width: 100%; background: var(--aid-surface); }
.form-control:focus { outline: none; border-color: var(--aid-accent); box-shadow: 0 0 0 2px rgba(15,110,99,0.12); }
.form-control::placeholder { color: var(--aid-text-dim); }
.form-hint { font-size: 12px; color: var(--aid-text-mute); margin: 0.25rem 0 0; }

/* Seed panels (E3 — document upload + website import) — token cards. */
.seed-intro { font-size: 13px; color: var(--aid-text-mute); margin: 0 0 1rem; line-height: 1.5; }
.seed-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.seed-panel {
  border: 1px solid var(--aid-border); border-radius: 10px; padding: 1.1rem; background: var(--aid-surface2);
  display: flex; flex-direction: column; gap: 0.6rem;
}
.seed-panel-head { display: flex; align-items: center; gap: 0.55rem; }
.seed-panel-icon {
  width: 32px; height: 32px; flex-shrink: 0; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  background: var(--aid-accent-soft); color: var(--aid-accent); font-size: 0.85rem;
}
.seed-panel-title { font-weight: 600; font-size: 13px; color: var(--aid-text); }
.seed-panel-desc { font-size: 12px; color: var(--aid-text-mute); line-height: 1.45; }
.seed-file-hidden { display: none; }
.seed-action { justify-content: center; }
.seed-url-row { display: flex; gap: 0.5rem; align-items: stretch; }
.seed-url-input { flex: 1; min-width: 0; }
.seed-url-btn { flex-shrink: 0; white-space: nowrap; }
.seed-file-name { font-size: 12px; color: var(--aid-text-mute); word-break: break-word; }
.seed-inline-error {
  display: flex; align-items: flex-start; gap: 0.35rem;
  font-size: 12px; color: var(--aid-danger-text); line-height: 1.4;
  background: var(--aid-danger-bg); border: 1px solid #fecaca; border-radius: 8px; padding: 0.5rem 0.65rem;
  i { margin-top: 0.1rem; flex-shrink: 0; }
}
.btn-spinner {
  width: 14px; height: 14px; border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite;
  display: inline-block; flex-shrink: 0;
}

/* States */
.loading-state, .empty-state, .error-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 3.5rem 1.5rem; text-align: center; }
.loading-spinner { width: 40px; height: 40px; border: 3px solid var(--aid-border); border-top-color: var(--aid-accent); border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 1rem; }
@keyframes spin { to { transform: rotate(360deg); } }
.loading-text { color: var(--aid-text-mute); font-size: 13px; font-weight: 500; }
.empty-icon, .error-icon { font-size: 3rem; margin-bottom: 1rem; opacity: 0.85; }
.empty-text, .error-text { font-size: 14px; font-weight: 600; color: var(--aid-text); margin-bottom: 0.5rem; }
.empty-subtext, .error-message { font-size: 13px; color: var(--aid-text-mute); max-width: 420px; line-height: 1.5; }
.error-message { color: var(--aid-danger-text); margin-bottom: 1rem; }
.retry-btn { margin-top: 0.25rem; }

/* Tablet (601–900px): seed panels can stay 2-up but tighten the gap; the row
   actions stay inline. */
@media (max-width: 900px) and (min-width: 601px) {
  .seed-grid { gap: 0.75rem; }
}

/* Mobile (≤600px, incl. 390px) */
@media (max-width: 600px) {
  .kb-page { padding: 0.75rem; gap: 1rem; }
  .panel-body { padding: 1rem; }
  .kb-toolbar { flex-direction: column; align-items: stretch; }
  .toolbar-left { justify-content: flex-start; }
  .toolbar-right { justify-content: stretch; }
  .toolbar-right .btn { flex: 1 1 auto; justify-content: center; min-height: 44px; }
  /* Row becomes a card: checkbox + content on top, actions full-width below. */
  .kb-row { flex-wrap: wrap; }
  .kb-row-actions { width: 100%; justify-content: flex-end; padding-top: 0.6rem; gap: 0.5rem; }
  .kb-row-actions .action-btn { min-height: 40px; padding: 0.45rem 0.7rem; }
  .fallback-row { flex-direction: column; align-items: stretch; }
  .fallback-select { max-width: 100%; width: 100%; }
  /* Seed panels stack; the website URL row goes full-width with the button below. */
  .seed-grid { grid-template-columns: 1fr; }
  .seed-url-row { flex-direction: column; }
  .seed-url-btn { width: 100%; min-height: 44px; }
  .seed-action { min-height: 44px; }
}
</style>
